import { newId } from '@gk/contracts'
import { DIAGNOSTIC_CODE, diagnosticCode, scrubMessage } from './scrub.ts'

/** The device-side error reporter (M11, ARCHITECTURE 17.1, "client diagnostics"): what goes wrong on a device (an uncaught error, a promise nobody handled, an error of the sync
 *  engine) becomes a CODE and a short scrubbed message, queued on the device and sent to `api.report_client_diagnostic`, which scrubs again, keeps it for the club's
 *  retention period (the policy's "client_diagnostic" category, proposed 30 days) and shows it on the "Santé de la synchro" page of the people who hold audit.read, with
 *  the device and the person signed in on it. Never a payload, an e-mail, a number, a precise coordinate, a URL query or quoted text (diagnostics/scrub.ts).
 *   - At most MAX_PER_HOUR reports are RECORDED in any hour (the same code and text within DEDUPE_MS counts once), and the queue keeps at most QUEUE_MAX, the oldest dropped
 *     first: a loop of errors can neither fill the device nor flood the server (which refuses more than CLIENT_DIAGNOSTIC_HOURLY_LIMIT a device and
 *     CLIENT_DIAGNOSTIC_PERSON_HOURLY_LIMIT a person in an hour anyway).
 *   - Offline-tolerant: the queue is kept in localStorage (one key per login and club, like the device's database), so what happened offline is sent at the next sync, and
 *     it survives a reload. Not in IndexedDB on purpose: the reporter must still work when IndexedDB itself is what fails (a lost IndexedDB connection is a classic error of
 *     iOS Home Screen apps), and an error handler can write it synchronously. A report older than MAX_AGE_MS waits no longer (the server answers TOO_OLD past the club's
 *     own period anyway).
 *   - A device the server does not know yet is registered by its first report (as by its first heartbeat), so the error that explains a failed first sync is not lost;
 *     while the server cannot register it yet (DEVICE_PENDING) the report is kept.
 *   - Batched: a flush sends at most FLUSH_BATCH reports, one RPC each, after a successful sync or a few seconds after a report while online. A network failure stops the
 *     flush and keeps the rest; a refusal of the server (not this club, not this device, a bad code) drops that report: a device never retries what will never be taken.
 *   - Each report has its own id, so a report sent again after a lost answer is stored once.
 *   - What is captured before a club's reporter is attached waits in memory only under the login and club it happened under (setDiagnosticContext), and goes only to a
 *     reporter of that same login and club; what happens with no login or no club open (the sign-in page, the club chooser, after a sign-out) is dropped: an error is never
 *     sent under another person's name or into another club. */

export const MAX_PER_HOUR = 20
export const QUEUE_MAX = 30
export const FLUSH_BATCH = 10
export const DEDUPE_MS = 10 * 60_000
/** How long a report may wait on THIS device before it is let go unsent: the device's own bound (it cannot read the club's policy), equal to the proposed default of the
 *  policy's "client_diagnostic" category. It is not the retention period: the server keeps a report for the club's own period and answers TOO_OLD past it. */
export const MAX_AGE_MS = 30 * 86_400_000
/** = private.client_diagnostic_hourly_limit() (pgTAP 46 pins it): what the server stores of one device in an hour. */
export const CLIENT_DIAGNOSTIC_HOURLY_LIMIT = 30
/** = private.client_diagnostic_person_hourly_limit() (pgTAP 46 pins it): what the server stores of one person in an hour, all their devices together. */
export const CLIENT_DIAGNOSTIC_PERSON_HOURLY_LIMIT = 60
/** What waits in memory, before a reporter is attached, under one login and club. */
export const EARLY_MAX = 10
const HOUR = 3_600_000
const PAUSE_MS = 15 * 60_000

export interface DiagEntry { id: string; at: number; code: string; message: string; app_version: string }
interface State { v: 1; queue: DiagEntry[]; recent: number[]; paused_until?: number }

export interface DiagStore { read(): State | null; write(s: State): void }
/** localStorage under one key; any failure (a private window, storage full or blocked) leaves the reporter working in memory. */
export function localStore(key: string): DiagStore {
  let mem: State | null = null
  return {
    read() {
      try { const raw = localStorage.getItem(key); if (raw) { const s = JSON.parse(raw) as State; if (s && s.v === 1 && Array.isArray(s.queue) && Array.isArray(s.recent)) return s } } catch { /* fall back to memory */ }
      return mem
    },
    write(s) { mem = s; try { localStorage.setItem(key, JSON.stringify(s)) } catch { /* memory only */ } },
  }
}
export const memoryStore = (): DiagStore => { let s: State | null = null; return { read: () => s, write: (n) => { s = n } } }

/** What the server said of one report. `retry`: keep it and stop this flush (offline, signed out, frozen, the device not registered yet); `pause`: the server's hourly limit,
 *  keep it and wait. */
export type SendOutcome = 'stored' | 'dropped' | 'retry' | 'pause'
export type DiagSender = (e: DiagEntry) => Promise<SendOutcome>

export class DiagnosticsReporter {
  private flushing: Promise<{ sent: number; dropped: number; kept: number }> | null = null
  private readonly clock: () => number
  private readonly idOf: () => string

  constructor(private readonly store: DiagStore, private readonly appVersion: string, opts: { clock?: () => number; newId?: () => string } = {}) {
    this.clock = opts.clock ?? Date.now
    this.idOf = opts.newId ?? newId
  }

  private state(): State {
    const s = this.store.read() ?? { v: 1, queue: [], recent: [] }
    const now = this.clock()
    return { ...s, queue: s.queue.filter((e) => now - e.at < MAX_AGE_MS), recent: s.recent.filter((t) => now - t < HOUR && t <= now) }
  }

  /** Queue one report; false when it was not recorded (the hourly cap, or the same code and text a moment ago). Never throws. */
  record(code: string, message: unknown, at?: number): boolean {
    try {
      const s = this.state()
      const now = this.clock()
      const c = DIAGNOSTIC_CODE.test(code) ? code : diagnosticCode(code)
      const m = scrubMessage(message)
      if (s.recent.length >= MAX_PER_HOUR) return false
      if (s.queue.some((e) => e.code === c && e.message === m && now - e.at < DEDUPE_MS)) return false
      s.queue.push({ id: this.idOf(), at: at ?? now, code: c, message: m, app_version: this.appVersion })
      if (s.queue.length > QUEUE_MAX) s.queue.splice(0, s.queue.length - QUEUE_MAX)
      s.recent.push(now)
      this.store.write(s)
      return true
    } catch { return false }
  }

  pending(): DiagEntry[] { return this.state().queue }

  /** Send what is queued, at most FLUSH_BATCH, oldest first. Single-flight; never throws. */
  flush(send: DiagSender): Promise<{ sent: number; dropped: number; kept: number }> {
    if (this.flushing) return this.flushing
    this.flushing = this.doFlush(send).finally(() => { this.flushing = null })
    return this.flushing
  }

  private async doFlush(send: DiagSender) {
    let sent = 0, dropped = 0
    const first = this.state()
    if ((first.paused_until ?? 0) > this.clock()) return { sent, dropped, kept: first.queue.length }
    for (const e of first.queue.slice(0, FLUSH_BATCH)) {
      let out: SendOutcome
      try { out = await send(e) } catch { out = 'retry' }
      if (out === 'retry' || out === 'pause') {
        if (out === 'pause') { const s = this.state(); s.paused_until = this.clock() + PAUSE_MS; this.store.write(s) }
        break
      }
      const s = this.state()
      s.queue = s.queue.filter((x) => x.id !== e.id)
      this.store.write(s)
      if (out === 'stored') sent++; else dropped++
    }
    return { sent, dropped, kept: this.state().queue.length }
  }
}

// ---- the window's errors -----------------------------------------------------------------------------------------------------------------
/** Noise that says nothing about the app: cross-origin script errors without details, the browser's own layout notice. */
const IGNORED = /^(Script error\.?|ResizeObserver loop (limit exceeded|completed with undelivered notifications)\.?)$/i

const errorText = (e: unknown): string => {
  if (e instanceof Error) return `${e.name}: ${e.message}`
  if (typeof e === 'string') return e
  return e === undefined || e === null ? '' : Object.prototype.toString.call(e)
}

/** Whose errors these are: the login signed in and the club open on this device. */
export interface DiagContext { userId: string; orgId: string }
const sameContext = (a: DiagContext | null, b: DiagContext | null): boolean => a !== null && b !== null && a.userId === b.userId && a.orgId === b.orgId

let context: DiagContext | null = null
let sink: DiagnosticsReporter | null = null
let sinkContext: DiagContext | null = null
let early: Array<{ code: string; message: string; at: number; ctx: DiagContext }> = []
let installed = false

/** Called once at start-up (main.tsx): from then on the window's uncaught errors and unhandled rejections are captured. */
export function installErrorCapture(win: Window = window): void {
  if (installed) return
  installed = true
  win.addEventListener('error', (ev: ErrorEvent) => {
    const text = ev.error !== undefined && ev.error !== null ? errorText(ev.error) : ev.message ?? ''
    if (!text || IGNORED.test(text.trim())) return
    capture('WINDOW_ERROR', text)
  })
  win.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => { capture('UNHANDLED_REJECTION', errorText(ev.reason)) })
}

/** The login and club open on this device (ClubGate sets it as soon as both are known, and clears it when either goes). What waited in memory under another one is
 *  dropped at once. */
export function setDiagnosticContext(c: DiagContext | null): void {
  if (!sameContext(c, context)) early = early.filter((e) => sameContext(e.ctx, c))
  context = c ? { userId: c.userId, orgId: c.orgId } : null
}

/** Report something from the app's own code (a code of its own, the text of the error). */
export function capture(code: string, message: unknown): void {
  try {
    if (sink && sameContext(sinkContext, context)) { sink.record(code, message); notify(); return }
    // no reporter of the current login and club yet: wait in memory under them, or, with no login or no club open, drop it
    if (context && early.length < EARLY_MAX) early.push({ code, message: scrubMessage(message), at: Date.now(), ctx: context })
  } catch { /* a reporter never throws */ }
}

let onRecorded: (() => void) | null = null
const notify = () => { try { onRecorded?.() } catch { /* ignore */ } }

/** The reporter of one login and club takes over the window's errors (that login and club are now the open ones), and what was captured before it under the same login and
 *  club (nothing else); `detach` lets go. */
export function attachReporter(r: DiagnosticsReporter, ctx: DiagContext, recorded?: () => void): () => void {
  setDiagnosticContext(ctx)
  sink = r; sinkContext = { userId: ctx.userId, orgId: ctx.orgId }; onRecorded = recorded ?? null
  const mine = early
  early = []
  for (const e of mine) r.record(e.code, e.message, e.at)
  if (mine.length) notify()
  return () => { if (sink === r) { sink = null; sinkContext = null; onRecorded = null } }
}

/** For tests: forget the window capture state. */
export function resetCaptureForTests(): void { context = null; sink = null; sinkContext = null; onRecorded = null; early = []; installed = false }
